import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@drpcrd/utils";

/**
 * The default card component that can be used to display content in a
 * card-like layout. All other cards should extend this base card component.
 * The default card comes out the box with padding 2, rounded-xl corners and a
 * border.
 *
 * @param onClick - Optional click handler for the card. If provided, the card will be clickable and add appropriate styling.
 */
export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, onClick, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "bg-card text-card-foreground flex flex-col gap-1 rounded-2xl border border-neutral-500 p-2 transition-all",
        onClick && "hover:shadow-light cursor-pointer",
        className,
      )}
      onClick={onClick}
      {...props}
    />
  ),
);
Card.displayName = "Card";

/**
 * CardHeader component for the top section of a card.
 *
 * @example
 * ```tsx
 * <CardHeader>
 *   <CardTitle>Card Title</CardTitle>
 * </CardHeader>
 * ```
 */
export const CardHeader = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

/**
 * CardTitle component for the card title.
 *
 * @example
 * ```tsx
 * <CardTitle>Title text</CardTitle>
 * ```
 */
export const CardTitle = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

/**
 * CardDescription component for card description text.
 *
 * @example
 * ```tsx
 * <CardDescription>Description text</CardDescription>
 * ```
 */
export const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-neutral-600", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

/**
 * CardContent component for the main content area of a card.
 *
 * @example
 * ```tsx
 * <CardContent>Main content</CardContent>
 * ```
 */
export const CardContent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("", className)} {...props} />
));
CardContent.displayName = "CardContent";

/**
 * CardFooter component for the bottom section of a card.
 *
 * @example
 * ```tsx
 * <CardFooter>
 *   <Button>Action</Button>
 * </CardFooter>
 * ```
 */
export const CardFooter = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex items-center", className)} {...props} />
));
CardFooter.displayName = "CardFooter";
